import "./index.scss";

import { InputField, LabelField, MyCheckbox } from "components";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ReactComponent as AddIcon } from "assets/icons/add-plus.svg";
import DeleteSvgComponent from "assets/icons/svg/DeleteSvg/Delete";
import AlertDialog from "components/Dialog";
import MyButton from "components/MyButton";
import StripeCard from "components/StripeCard/StripeCard";
import TranzilaCard from "components/TranzilaCard/TranzilaCard";
import { checkIsStripeByPaymentGateway, checkIsTranzilaByPaymentGateway } from "helper/checkIsStripe";
import { getCurrency, numberFormat } from "helper/numberFormat";
import { postalCodeFormat, postalCodeLabel } from "helper/postalCodeFormat";
import useGetUserAccount from "hook/useGetUserAccount";
import { toastr } from "react-redux-toastr";
import FinanceService from "../../../../Lib/API/Finance";
import AddInstallmentsPayment from "./AddInstallmentsPayment";
import { getAccessTokenSilently } from "utils/functions";
import { Fonts, FontWeights, FontColors } from "theme/fontConfig";

export const CreditCard = ({
  formik,
  title,
  onPaymentValueChange,
  selected,
  setSelected,
  enablePaymentInstallments,
  totalPaidAmount,
  calculateTotalAmount,
  allowPaymentInstallment,
  paymentGateway,
}) => {
  const enableInstallments = useMemo(
    () => allowPaymentInstallment && enablePaymentInstallments,
    [allowPaymentInstallment, enablePaymentInstallments]
  );

  const [addNewCard, setAddNewCard] = useState(false);
  const [cards, setCards] = useState([]);
  const [cardDetail, setCardDetail] = useState({});
  const [addCardLoading, setAddCardLoading] = useState(false);
  const [creditId, setCreditId] = useState(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [setDefaultLoading, setSetDefaultLoading] = useState(false);
  const [checkCard, setCheckCard] = useState(false);

  let accountID = localStorage.getItem("accountID")
    ? localStorage.getItem("accountID")
    : "";

  const postalLabel = postalCodeLabel();

  const customStyles = {
    width: 73,
    marginInline: "auto",
  };

  const accountData = useGetUserAccount({ member: true });

  const isStripe = useMemo(
    () => checkIsStripeByPaymentGateway(paymentGateway),
    [paymentGateway]
  );

  const isTranzila = useMemo(
    () => checkIsTranzilaByPaymentGateway(paymentGateway),
    [paymentGateway]
  );

  const [tranzilaCurrency, setTranzilaCurrency] = useState("ILS");

  const getCreditCards = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const res = await FinanceService.getCreditCardsByAccountId(
      token,
      accountID ?? ""
    );
    setCards(res.card.data);
    const already = res.card.data?.find((item) => item?.is_default === true);
    if (already) {
      formik.setFieldValue("cardDetail", [already]);
      setSelected(already);
    } else {
      formik.setFieldValue("cardDetail", []);
      setSelected({});
    }
  }, [getAccessTokenSilently, accountID]);

  const onCardDetailChange = (event, name) => {
    setCardDetail({
      ...cardDetail,
      [name]: event.target.value,
    });
    formik.setFieldValue(`cardDetails.${name}`, event.target.value);
  };

  useEffect(() => {
    if (accountData) {
      accountID = accountData?.user?.permissions?.linked_account?._id;
      getCreditCards();
    } else {
      getCreditCards();
    }
  }, [getCreditCards, accountData]);

  useEffect(() => {
    if (selected) {
      onPaymentValueChange(title, {
        target: {
          value: title,
          name: "paymentMethod",
        },
      });
      onPaymentValueChange(title, {
        target: {
          value: selected.id,
          name: "cardId",
        },
      });

      formik.setFieldValue("last4", selected.last4);
    }
    //eslint-disable-next-line
  }, [selected]);

  const onSaveCard = async (tokenId = "", tranzilaResult = null) => {
    const cardsDetails = {
      object: "card",
      ...cardDetail,
    };

    if ((!cardDetail?.cvc || cardDetail?.cvc === "") && !tokenId) {
      return toastr.error("Error", "Please add CVC.");
    }
    if (cardDetail?.cvc?.length < 3 && !tokenId) {
      return toastr.error("Error", "Please enter valid CVC.");
    }

    if (!tokenId && (!cardDetail?.zip || !cardDetail?.zip?.length)) {
      return toastr.error("Error", `Please enter ${postalCodeLabel()}.`);
    }

    const validZipCode = postalCodeFormat(cardDetail?.zip);
    if (!tokenId && !validZipCode) {
      toastr.info("Validation", `Invalid ${postalLabel}`);
    }

    const bodyData = {};
    if (isStripe && tokenId) {
      bodyData.token = tokenId;
    } else if (isTranzila && tokenId && tranzilaResult) {
      bodyData.token = tokenId;
      bodyData.cardsDetails = {
        object: 'card',
        number: tokenId,
        exp_month: tranzilaResult.expmonth ? String(Number(tranzilaResult.expmonth)) : undefined,
        exp_year: tranzilaResult.expyear ? `20${tranzilaResult.expyear}` : undefined,
      };
      bodyData.tranzilaData = tranzilaResult;
      bodyData.currency = tranzilaCurrency;
    } else {
      bodyData.cardsDetails = cardsDetails;
    }
    const token = await getAccessTokenSilently();

    FinanceService.addCreditCard(token, accountID ?? "", bodyData)
      .then(() => {
        getCreditCards();
        formik.setFieldValue(`cardDetails.exp_month`, "");
        formik.setFieldValue("cardDetails.exp_year", "");
        formik.setFieldValue("cardDetails.cvc", "");
        formik.setFieldValue("cardDetails.number", "");
        formik.setFieldValue("cardDetails.zip", "");
        setCardDetail({});
        toastr.success("Success", "Card Added Successfully");
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setAddCardLoading(false);
      });
  };

  const onDelete = async (idx) => {
    const token = await getAccessTokenSilently();
    FinanceService.deleteCreditCard(token, idx, accountID ?? "")
      .then((res) => {
        if (res?.card?.messages?.resultCode == "Ok") {
          toastr.success("Success", "Card has been delete successfully ");
        }
        getCreditCards();
        setSelected(null);
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message);
      })
      .finally(() => {
        closeConfirmDialog();
      });
  };

  const onSelect = (data, e) => {
    if (setDefaultLoading) return;

    if (data?.id === selected?.id) {
      formik.setFieldValue("cvc", "");
      formik.setFieldValue("cardDetail", []);
      setSelected({});
    } else {
      formik.setFieldValue("cvc", "");
      formik.setFieldValue("cardDetail", data);
      setSelected({ ...data });
    }
  };
  useEffect(() => {
    const already = formik?.values?.payments?.find(
      (item) => item.paymentMethod === title
    );
    if (already) {
      const current = cards?.find((item) => item.id === already.cardId);
      setSelected(current);
    }
  }, [cards]);

  const openConfirmDialog = (id) => {
    setCreditId(id);
    setOpenAlert(true);
  };

  const closeConfirmDialog = () => {
    setOpenAlert(false);
    setCreditId(null);
  };

  const setCardDefault = async () => {
    if (selected?.id) {
      const token = await getAccessTokenSilently();
      const data = {
        accountId: accountID ?? "",
        cardId: selected?.id,
      };
      setSetDefaultLoading(true);
      await FinanceService.setDefaultCreditCard(data, token)
        .then((res) => {
          toastr.success("Success", "Default card is checked!");
        })
        .catch((err) => {
          toastr.error("Error", err?.data?.message);
        })
        .finally(() => {
          const timeOut = setTimeout(() => {
            setSetDefaultLoading(false);
          }, 2000);
          return () => {
            clearTimeout(timeOut);
          };
        });
      getCreditCards();
    }
  };
  useEffect(() => {
    setCardDefault();
  }, [checkCard]);

  const calculateRemainingBalance = () => {
    return calculateTotalAmount() - totalPaidAmount;
  };

  const isOptionalObject = useMemo(() => {
      if (
        !Array.isArray(formik["values"]["allPaymentsMethods"]) ||
        formik["values"]["allPaymentsMethods"].length === 0
      )
        return;
  
      const currentComponent = formik["values"]["allPaymentsMethods"].find(
        (item) => {
          return item.type === "credit card";
        }
      );
  
      if (!currentComponent ||!currentComponent._id) return;
  
      return  { 
         id:currentComponent?._id,
         coverConvenienceFee: currentComponent?.coverConvenienceFee ?? false, 
        isOptional:currentComponent?.convenienceFeeInfo?.optional ?? false,
          message:currentComponent?.convenienceFeeInfo?.message,
          type: currentComponent?.type,
      }
    }, [formik["values"]]);  

  useEffect(() => {
  if (!isOptionalObject?.isOptional) return;
  if (!key) return;

  if (formik.values[key] === undefined) {
    formik.setFieldValue(key, true);
  }
}, [key, isOptionalObject]);

  const key = `${isOptionalObject?.id}`;

  return (
    <div className="credit-card mt-5">
      <div className="form-row col-sm-12 px-0 justify-content-between">
        <div className="d-flex col-sm-7 ">
          <div className="cards-radio font-16">
            {cards?.map((item) => (
              <div
                data-id={item.id}
                key={item.id}
                onClick={() => onSelect(item)}
                className={`${
                  selected?.id === item.id
                    ? "selected-card card-item"
                    : "card-item"
                } ${setDefaultLoading ? "disabled-card" : ""}`}
              >
                <p>
                  {`${item.country ? item.country : ""} ${
                    item.brand ? item.brand : ""
                  } (${item.last4 ? item.last4 : ""}${item.currency === 'USD' ? ' $' : item.currency === 'ILS' ? ' ₪' : ''}), Exp: ${item.exp_month}/${
                    item.exp_year
                  }`}

                  <DeleteSvgComponent
                    color={"gray"}
                    onClick={() => {
                      openConfirmDialog(item.id);
                    }}
                  />

                  {((item.hasOwnProperty("address_zip") &&
                    !item?.address_zip) ||
                    (item.hasOwnProperty("zip") &&
                      item?.zip === "zip is missing")) && (
                    <span className="text-danger missing-zip">
                      {postalLabel} is missing
                    </span>
                  )}
                </p>
              </div>
            ))}
          </div>
          {/* <MyRadioGroup
            handleChange={(val) => {
              setSelected(val);
            }}
            name="cards"calculateRemainingBalance
            className="cards-radio"
            formsControls={[]}
          /> */}
        </div>
        <div
          className="right-side px-0"
          style={{
            minWidth: "216px",
          }}
        >
          {!!cards?.length && (
            <>
              <div className="d-flex">
                <MyCheckbox
                  name={"default"}
                  checked={selected ? selected?.is_default : false}
                  title=""
                  value={checkCard}
                  handlechange={(e) => {
                    setCheckCard(!checkCard);
                  }}
                />
                <LabelField title="Set as default credit card" />
              </div>
            </>
          )}
        </div>
        <div className="my-5 w-100 d-flex col-sm-7 ">
          <div
            className="add-new-payment-info w-100"
            onClick={() => setAddNewCard(!addNewCard)}
          >
            <span className="add-icon">
              <AddIcon />
            </span>
            <span className="add">Add New Card</span>
          </div>
        </div>

        {!!cards?.length && enableInstallments && (
          <div
            className="right-side px-0"
            style={{
              minWidth: "216px",
            }}
          >
            <div
              className="d-flex align-items-center"
              style={
                formik.values?.allowAchInstallment
                  ? { opacity: 0.7, pointerEvents: "none" }
                  : {}
              }
            >
              <MyCheckbox
                name={"Installments-pay"}
                checked={formik.values.allowCreditInstallment}
                title=""
                handlechange={(e) => {
                  if (!formik?.values?.creditCardAmount) {
                    formik.setFieldValue(
                      "creditCardAmount",
                      calculateRemainingBalance()
                    );
                  } else {
                    formik.setFieldValue("allowAchInstallment", false);
                    formik.setFieldValue("installmentAchDetails", {
                      cycle: "",
                      frequency: "",
                      startInstallment: new Date(),
                    });
                  }
                  formik.setFieldValue(
                    "allowCreditInstallment",
                    e.target.checked
                  );
                }}
              />
              <LabelField title="Pay in Installments" />
            </div>
          </div>
        )}

                  {!!cards?.length &&isOptionalObject?.coverConvenienceFee && isOptionalObject?.isOptional && (
                           <div
                        className="left-side px-0"
                        style={{
                         minWidth: "216px",
                   justifyContent: "flex-start",
    display: "flex",
    width: "89%",
                        }}
                      >
                        <div className="d-flex align-items-baseline  mb-2">
                            <MyCheckbox
                             name={key}
                    checked={formik.values?.[key]}
                    handlechange={(e) => {
                     
                      formik.setFieldValue(
                        key,
                        e.target.checked
                      );
                    }}
                            />
                            <LabelField  style={{ display: "flex", alignItems: "center",  marginBottom: 0,    }}  title={isOptionalObject?.message} />
                          </div>
                           </div>
                        )}
      </div>
      {addNewCard && (
        <>
          {isTranzila && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: Fonts.GeneralLabelFont.fontSize, fontWeight: Fonts.GeneralLabelFont.fontWeight }}>Currency:</span>
              {["ILS", "USD"].map(cur => (
                <button
                  key={cur}
                  type="button"
                  onClick={() => setTranzilaCurrency(cur)}
                  style={{
                    padding: "6px 16px",
                    borderRadius: 6,
                    border: tranzilaCurrency === cur ? "2px solid #2555EF" : "1px solid #e2e8f0",
                    background: tranzilaCurrency === cur ? "#EEF2FF" : "#fff",
                    color: tranzilaCurrency === cur ? "#2555EF" : "#333",
                    fontWeight: tranzilaCurrency === cur ? FontWeights.SemiBold : FontWeights.Regular,
                    fontSize: Fonts.GeneralButtonFont.fontSize,
                    cursor: "pointer",
                  }}
                >
                  {cur === "ILS" ? "₪ ILS" : "$ USD"}
                </button>
              ))}
            </div>
          )}
          {isTranzila ? (
            <TranzilaCard
              onSaveCard={onSaveCard}
              loading={addCardLoading}
              setLoading={setAddCardLoading}
              saveCard={true}
              currency={tranzilaCurrency}
              terminalName={paymentGateway?.keys?.terminal_name}
              thtk={null}
              sandbox={false}
            />
          ) : isStripe ? (
            <StripeCard
              onSaveCard={onSaveCard}
              loading={addCardLoading}
              setLoading={setAddCardLoading}
              saveCard={true}
              publicKey={paymentGateway?.keys?.publicKey}
            />
          ) : (
            <div className="row">
              <div className="col-sm-4 pr-0">
                <InputField
                  mandatory
                  {...{
                    formik,
                    title: "Card Number",
                    name: "cardDetails.number",
                    type: "string",
                    minlength: "16",
                    maxLength: "16",
                    value: formik.values?.cardDetails?.number ?? "",
                  }}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  handleChange={(event) => onCardDetailChange(event, "number")}
                />
              </div>
              <div className="col-sm-2">
                <InputField
                  mandatory
                  {...{
                    formik,
                    title: "Expiry Month",
                    name: `cardDetails.exp_month`,
                    placeholder: "MM",
                    minlength: "1",
                    maxLength: "2",
                    type: "string",
                    value: formik.values?.cardDetails?.exp_month ?? "",
                  }}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  handleChange={(event) =>
                    onCardDetailChange(event, "exp_month")
                  }
                />
              </div>
              <div className="col-sm-2">
                <InputField
                  mandatory
                  {...{
                    formik,
                    title: "Expiry Year",
                    type: "string",
                    minlength: "4",
                    maxLength: "4",
                    name: `cardDetails.exp_year`,
                    placeholder: "YYYY",
                    value: formik.values?.cardDetails?.exp_year ?? "",
                  }}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  handleChange={(event) =>
                    onCardDetailChange(event, "exp_year")
                  }
                />
              </div>
              <div className="col-sm-2 px-0">
                <InputField
                  mandatory
                  {...{
                    formik,
                    title: "CVC",
                    type: "string",
                    minlength: "3",
                    maxLength: "4",
                    name: `cardDetails.cvc`,
                    placeholder: "3-4 digits",
                    value: formik.values?.cardDetails?.cvc ?? "",
                  }}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  handleChange={(event) => onCardDetailChange(event, "cvc")}
                />
              </div>
              <div className="col-sm-2">
                <InputField
                  mandatory
                  {...{
                    formik,
                    title: postalLabel,
                    name: `cardDetails.zip`,
                    placeholder: postalLabel,
                    type: "string",
                    value: formik.values?.cardDetails?.zip ?? "",
                  }}
                  handleChange={(event) => onCardDetailChange(event, "zip")}
                />
              </div>
              <div className="col-sm-4 mb-5 pb-5">
                <MyButton
                  content={"Save Card"}
                  style={{ width: 120 }}
                  onClick={() => {
                    onSaveCard();
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}
      {formik.values.allowCreditInstallment && enableInstallments && (
        <AddInstallmentsPayment
          formik={formik}
          formikKey="installmentCreditDetails"
          installmentFieldLabel="Installments"
          dateFieldLabel="First payment date"
          dateRequired={true}
        />
      )}
      <AlertDialog
        open={openAlert}
        handleClose={() => {
          closeConfirmDialog();
        }}
        onDecline={() => {
          setOpenAlert(false);
        }}
        onAccept={() => {
          onDelete(creditId);
        }}
        content={"Are you sure you want to delete this credit card?"}
        contentStyle={{
          fontSize: Fonts.GeneralXLargeBodyFont.fontSize,
        }}
      />
    </div>
  );
};
export const CreditCardHeader = ({ selected, formik, ...props }) => {
  const { expanded, current } = props;
  const [disableInput, setDisableInput] = useState(true);
  const currency = getCurrency();

  const preventMinus = (e) => {
    const characterCode = e.key;
    if (characterCode === "Backspace") return;

    const characterNumber = Number(characterCode);
    if (characterNumber >= 0 && characterNumber <= 9) {
      if (e.currentTarget.value && e.currentTarget.value.length) {
        return;
      } else if (characterNumber === 0) {
        e.preventDefault();
      }
    } else {
      e.preventDefault();
    }
  };
  const handlefieldClick = (e) => {
    if (e.stopPropagation && expanded === true) {
      e.stopPropagation();
    }
    if (!selected?.id && e.stopPropagation && expanded === true) {
      e.stopPropagation();
    }
  };

  const getCommaSeperatedValue = (num) => {
    return parseFloat(num?.toString().replace(/,/g, ""))
      .toFixed(2)
      ?.toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <div
      className={
        expanded === false && !formik?.values?.creditCardAmount
          ? "accordion-payment-header"
          : expanded === false &&
            current?.includes("credit card") === false &&
            formik?.values?.creditCardAmount
          ? "accordion-header"
          : expanded === false
          ? "accordion-header"
          : expanded === false
          ? "accordion-payment-header accordion-header "
          : expanded === true
          ? "accordion-header"
          : "accordion-header-payment"
      }
    >
      <LabelField title="Credit Card" />

      <div
        className={
          expanded === false && formik?.values?.creditCardAmount
            ? "custom-expand dollor-icon col-sm-3 nonexpanded-amount"
            : "col-sm-3"
        }
      >
        {expanded === false && formik?.values?.creditCardAmount ? (
          <div className="cash-non-expanded">
            <span>{currency}</span>
            <span>
              {getCommaSeperatedValue(formik?.values?.creditCardAmount)}
            </span>
          </div>
        ) : current?.includes("credit card") && expanded === true ? (
          <InputField
            {...{
              formik,
              title: "none",
              name: `amount`,
              style: { background: "#ffff", border: "none" },
              placeholderIcon: (
                <span
                  className="align-self-center px-4"
                  style={{ color: FontColors.Black }}
                >
                  {currency}
                </span>
              ),
              value: formik?.values?.creditCardAmount,
            }}
            // disabled={
            //   selected?.id && formik?.values?.cvc
            //     ? setDisableInput === false
            //     : true
            // }
            // onKeyDown={preventMinus}
            handleChange={props.onCreditCardAmountChange}
            paymentInput="paymentInput"
            handleFocus={(e) => handlefieldClick(e)}
            // disabled
          />
        ) : expanded === false &&
          current?.includes("credit card") === false &&
          formik?.values?.creditCardAmount ? (
          <div className="cash-non-expanded">
            <span>
              {numberFormat({
                number: formik?.values?.creditCardAmount,
                decimal: 2,
                style: "currency",
              })}
            </span>
          </div>
        ) : (
          ""
        )}
      </div>
    </div>
  );
};
